import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";
import ChangePasswordDialog from "./ChangePasswordDialog";
import { toast } from "sonner";
import { Lightning, SignOut, UserCircle, ArrowsLeftRight, Barbell, User as UserIcon } from "@phosphor-icons/react";

export default function Header() {
  const { user, logout, setActiveRole, formatApiError } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const navClass = ({ isActive }) =>
    `text-xs font-bold uppercase tracking-[0.18em] transition-colors ${
      isActive ? "text-white" : "text-zinc-400 hover:text-white"
    }`;

  const active = user?.active_role || user?.role;
  const roles = user?.roles || (user?.role ? [user.role] : []);
  const canSwitch = roles.includes("client") && roles.includes("instructor");
  const isClientOnly = roles.length === 1 && roles[0] === "client";

  const switchRole = async (r) => {
    try {
      await setActiveRole(r);
      toast.success(t("header.role_switched", { role: r === "instructor" ? t("auth.role_instructor") : t("auth.role_client") }));
      navigate(r === "instructor" ? "/instructor" : "/");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <header
      className="sticky top-0 z-50 border-b border-white/10 backdrop-blur-xl"
      style={{ background: "rgba(5,5,5,0.75)" }}
      data-testid="app-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <Lightning size={26} weight="fill" className="text-[#E30613]" />
          <span className="font-display font-black text-xl uppercase tracking-tight">
            FitAgenda <span className="text-[#E30613]">CH</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 flex-1 justify-center">
          <NavLink to="/" end className={navClass} data-testid="nav-agenda">{t("header.agenda")}</NavLink>
          <a href="/#come-funziona" className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-400 hover:text-white transition-colors" data-testid="nav-how">{t("header.how_it_works")}</a>
          <NavLink to="/instructors" className={navClass} data-testid="nav-instructors">{t("header.instructors")}</NavLink>
          {user && active === "client" && (
            <NavLink to="/my-bookings" className={navClass} data-testid="nav-my-bookings">{t("header.my_bookings")}</NavLink>
          )}
          {user && active === "instructor" && (
            <NavLink to="/instructor" className={navClass} data-testid="nav-instructor-dash">{t("header.instructor_dashboard")}</NavLink>
          )}
          {user && roles.includes("admin") && (
            <NavLink to="/admin" className={navClass} data-testid="nav-admin-dash">{t("header.admin")}</NavLink>
          )}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <LanguageSwitcher />
          {!user || user === false ? (
            <>
              <Link to="/login" data-testid="header-login-link">
                <Button variant="ghost" className="text-xs uppercase tracking-wider font-bold rounded-none">{t("header.login")}</Button>
              </Link>
              <Link to="/register" data-testid="header-register-link">
                <Button className="bg-[#E30613] hover:bg-[#FF1A27] text-white text-xs uppercase tracking-wider font-bold rounded-none">
                  {t("header.register")}
                </Button>
              </Link>
            </>
          ) : (
            <div className="flex items-center gap-3">
              {canSwitch && (
                <div className="hidden lg:flex items-center border border-white/15" data-testid="role-switch">
                  <button
                    onClick={() => switchRole("client")}
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 transition-colors ${active === "client" ? "bg-[#E30613] text-white" : "text-zinc-400 hover:text-white"}`}
                    data-testid="role-switch-client"
                  >
                    <UserIcon size={12} weight="fill" /> {t("auth.role_client")}
                  </button>
                  <button
                    onClick={() => switchRole("instructor")}
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 transition-colors ${active === "instructor" ? "bg-[#E30613] text-white" : "text-zinc-400 hover:text-white"}`}
                    data-testid="role-switch-instructor"
                  >
                    <Barbell size={12} weight="fill" /> {t("auth.role_instructor")}
                  </button>
                </div>
              )}
              {isClientOnly && (
                <Link
                  to="/become-instructor"
                  className="hidden lg:inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border border-[#E30613] text-[#E30613] hover:bg-[#E30613] hover:text-white transition-colors"
                  data-testid="become-instructor-cta"
                >
                  <ArrowsLeftRight size={12} weight="bold" /> {t("header.become_instructor")}
                </Link>
              )}
              <span className="hidden md:flex items-center gap-2 text-xs text-zinc-300" data-testid="header-user-name">
                <UserCircle size={20} className="text-zinc-500" />
                <span className="font-semibold">{user.name}</span>
                <span className="text-zinc-500">·</span>
                <span className="uppercase tracking-wider text-[10px] text-[#E30613] font-bold">{active}</span>
              </span>
              <Link to="/profile" data-testid="header-profile-link"
                className="text-xs uppercase tracking-wider font-bold text-zinc-400 hover:text-white px-3 py-1.5">
                {t("profile.link_label")}
              </Link>
              <ChangePasswordDialog />
              <Button
                variant="ghost"
                onClick={async () => { await logout(); navigate("/"); }}
                className="text-xs uppercase tracking-wider font-bold rounded-none"
                data-testid="logout-button"
              >
                <SignOut size={16} className="mr-1" />{t("header.logout")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
